import clsx from "clsx";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { LeadLink } from "web/react/components/lead-link";
import SaveForLaterButton from "web/react/components/save-for-later-button/save-for-later-button";
import { SizePicker } from "web/react/components/size-picker/size-picker";
import { ProductCardHorizontal } from "web/react/emo/product-card-horizontal";
import { View } from "web/react/emo/view";
import {
    EventLabel,
    PinSizeInfo,
    useSaveForLater,
} from "web/react/hooks/use-save-for-later/use-save-for-later";
import {
    baseAffiliateAnalyticsConfig,
    sendAnalyticsEvent,
} from "web/react/pages/product/out-of-stock/out-of-stock-product-page/utils";
import { gettext } from "web/script/modules/django-i18n";
import userProfiler from "web/script/modules/userprofiler";
import {
    ProductBuyOptionsSerializer,
    ProductLayoutSerializer,
    ProductPageAreaSerializer,
    ProductScreenSize,
} from "web/types/serializers";
import * as styles from "./product-area.css";
import { SeeMoreButton } from "./see-more-button/see-more-button";

export interface ProductAreaProps {
    productArea: ProductPageAreaSerializer;
    productLayout: ProductLayoutSerializer;
    uniqueRetailerCount: number;
    isCheckout: boolean;
    productDetailsContainerRef: React.RefObject<HTMLDivElement>;
    mainBuyOption: ProductBuyOptionsSerializer | null;
}

export function ProductArea({
    productArea,
    productLayout,
    uniqueRetailerCount,
    isCheckout,
    productDetailsContainerRef,
    mainBuyOption,
}: ProductAreaProps): React.ReactElement {
    const [selectedSize, setSelectedSize] = useState<ProductScreenSize | undefined>(undefined);
    const [isSizePickerExpanded, setSizePickerExpanded] = useState<boolean>(false);
    const [hasUserSeenSizePicker, setHasUserSeenSizePicker] = useState(false);
    const schemas = productArea.sizes.schema_labels;
    const userDefaultSchema = productArea.sizes.user_default_schema;
    const { isSaved: productIsSaved, toggleSaveForLater } = useSaveForLater(
        productArea.product.product_id,
        EventLabel.OOS
    );

    const sizePickerRef = useRef<HTMLDivElement>(null);

    const isUserLoggedIn = userProfiler.isLoggedIn();

    const productId = productArea.product.product_id;

    const productIsSavedButtonText =
        uniqueRetailerCount > 1
            ? gettext("oos_product.tracking_mutiple_retailers_notification", {
                  retailer_count: uniqueRetailerCount,
              })
            : gettext("oos_product.tracking_single_retailer_notification");

    const sizes = productArea.sizes.all_sizes;
    const hasSizes = sizes.length > 0 && sizes.every((size) => size.localized_sizes);

    const showSizePicker = hasSizes && productIsSaved;

    const convertSizeToPinInfo = useCallback((size: ProductScreenSize): PinSizeInfo[] | null => {
        if (!size.localized_sizes || !size.user_default_schema) {
            return null;
        }
        const localizedSize = size.localized_sizes[size.user_default_schema];

        if (!localizedSize || !localizedSize.tag) {
            return null;
        }

        // The tag might be a JSON string like:
        // '{"first_dimension": "size.jeans.au.w.16", "second_dimension": "size.jeans.inseam.inch.30"}'
        const sizeTag = localizedSize.tag;
        let pinSizes: PinSizeInfo[] = [];
        if (sizeTag.startsWith("{")) {
            try {
                const parsedTag = JSON.parse(sizeTag);
                pinSizes = Object.values(parsedTag).map((dimensionTag: any) => ({
                    size_tag: dimensionTag,
                }));
            } catch (e) {
                console.warn(
                    `Failed to parse multi-dimensional size tag: ${sizeTag}, threw error ${e}`
                );
            }
        } else {
            pinSizes.push({ size_tag: sizeTag }); // tag: "size.dress.uk.10"
        }
        return pinSizes;
    }, []);

    const handleSizePickerSelect = useCallback(
        (option: any) => {
            const size = option.value as ProductScreenSize;
            const sizeInfo = convertSizeToPinInfo(size);
            const isUserUpdatingSize = selectedSize !== undefined;
            setSelectedSize(size);
            setSizePickerExpanded(false);

            if (sizeInfo) {
                toggleSaveForLater(sizeInfo);
                if (isUserUpdatingSize) {
                    sendAnalyticsEvent("oos_stock_alert", "updated", "", true, {
                        product_id: productId,
                        sizes: sizeInfo,
                    });
                } else {
                    sendAnalyticsEvent("oos_stock_alert", "clicked", "select_size", true, {
                        product_id: productId,
                        sizes: sizeInfo,
                    });
                }
            }
        },
        [convertSizeToPinInfo, toggleSaveForLater, productId, selectedSize]
    );

    const trackCheckoutClick = useCallback(
        (label: string) => {
            if (isCheckout && mainBuyOption) {
                sendAnalyticsEvent("oos-product-hero", "click", label, true, {
                    buy_option_id: mainBuyOption.link_id.toString(),
                    product_id: productId,
                    retailer_slug: mainBuyOption.retailer_slug,
                });
            }
        },
        [isCheckout, mainBuyOption, productId]
    );

    const trackCheckoutClickAndScroll = useCallback(
        (label: string) => {
            mainBuyOption && trackCheckoutClick(label);
            productDetailsContainerRef.current?.scrollIntoView({ behavior: "smooth" });
        },
        [trackCheckoutClick, productDetailsContainerRef, mainBuyOption]
    );

    const ImageContainer = useCallback(
        ({ children }: { children: React.ReactNode }): React.ReactElement => {
            if (isCheckout || !mainBuyOption) {
                return (
                    <button
                        onClick={() => trackCheckoutClickAndScroll("checkout-gallery-image")}
                        data-testid="product-image"
                        className={styles.cursorPointer}
                    >
                        {children}
                    </button>
                );
            } else {
                const baseAnalytics = baseAffiliateAnalyticsConfig(mainBuyOption, productId);
                return (
                    <LeadLink
                        {...baseAnalytics}
                        openInNewTab
                        reason="affiliate-gallery-image"
                        eventLabel="affiliate-gallery-image"
                        dataTestId="product-image"
                    >
                        {children}
                    </LeadLink>
                );
            }
        },
        [isCheckout, mainBuyOption, trackCheckoutClickAndScroll, productId]
    );

    const ViewProductDetailsContainer = useCallback(
        ({ children }: { children: React.ReactNode }): React.ReactElement => {
            if (isCheckout || !mainBuyOption) {
                return (
                    <button
                        onClick={() => trackCheckoutClickAndScroll("checkout-view-product-details")}
                        data-testid="view-product-details"
                        className={clsx(styles.semanticButton, styles.cursorPointer)}
                    >
                        {children}
                    </button>
                );
            } else {
                const baseAnalytics = baseAffiliateAnalyticsConfig(mainBuyOption, productId);
                return (
                    <LeadLink
                        {...baseAnalytics}
                        openInNewTab
                        reason="affiliate-view-product-details"
                        eventLabel="affiliate-view-product-details"
                        dataTestId="view-product-details"
                    >
                        {children}
                    </LeadLink>
                );
            }
        },
        [isCheckout, mainBuyOption, trackCheckoutClickAndScroll, productId]
    );

    useEffect(() => {
        if (showSizePicker && !hasUserSeenSizePicker) {
            sendAnalyticsEvent("oos_stock_alert_size_picker", "impression", "", false, {
                product_id: productId,
            });
            setHasUserSeenSizePicker(true);
        }
    }, [showSizePicker, productId, hasUserSeenSizePicker]);

    return (
        <View width="full" data-testid="product-area">
            <ProductCardHorizontal
                product={productArea.product}
                designer={productArea.designer}
                buyOption={mainBuyOption}
                ImageContainer={ImageContainer}
                ViewProductDetailsContainer={ViewProductDetailsContainer}
            >
                {uniqueRetailerCount > 0 && (
                    <SaveForLaterButton
                        type="cta"
                        cta={{
                            title: gettext("oos_product.get_notified_when_back_in_stock_btn"),
                        }}
                        initialIsSaved={productIsSaved}
                        activeCta={{
                            title: productIsSavedButtonText,
                            icon: "notification-active",
                            iconPosition: "right",
                        }}
                        {...(isUserLoggedIn && { onClick: toggleSaveForLater })}
                        pageSource="oos"
                        productId={productId}
                        isLoggedIn={isUserLoggedIn}
                        size="lg"
                        variant="primary"
                        width="full"
                        beforeOnClick={() => {
                            sendAnalyticsEvent(
                                "oos-notification-alert",
                                productIsSaved ? "toggle_off" : "toggle_on",
                                "pdp_button",
                                true,
                                {
                                    product_id: productId,
                                }
                            );
                        }}
                    />
                )}
                {showSizePicker && (
                    <div ref={sizePickerRef}>
                        <SizePicker
                            onSelect={handleSizePickerSelect}
                            isExpanded={isSizePickerExpanded}
                            schemas={schemas}
                            defaultSelectText={gettext(
                                "oos_product.select_size_for_personalized_alerts"
                            )}
                            userDefaultSchema={userDefaultSchema}
                            setExpanded={(expanded: boolean): void => {
                                setSizePickerExpanded(expanded);
                                if (expanded) {
                                    sendAnalyticsEvent("oos_stock_alert", "opened", "", true, {
                                        product_id: productId,
                                    });
                                }
                            }}
                            sizes={sizes}
                            productType={productArea.product_purchase_type}
                            selectedOption={selectedSize}
                            isExpressCheckout={false}
                            isComparison={false}
                            showDisabledOptions={true}
                            showAllSchemas={true}
                            selectedTextPrefix={
                                selectedSize
                                    ? ` ${gettext("oos_product.alert_set_for_size")} `
                                    : undefined
                            }
                        />
                    </div>
                )}
                <SeeMoreButton
                    productArea={productArea}
                    productLayout={productLayout}
                    mainBuyOption={mainBuyOption}
                    isCheckout={isCheckout}
                    trackCheckoutClick={trackCheckoutClick}
                />
            </ProductCardHorizontal>
        </View>
    );
}
